import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { letterApi } from '../services/letterApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TurndownService from 'turndown';
import './PrepLetters.css';

const PrepLetters = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [letters, setLetters] = useState([]);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProgress, setUserProgress] = useState({ current: 1, completed: 0 });
  const [quickJumpValue, setQuickJumpValue] = useState('');
  const [letterContent, setLetterContent] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [lettersPerPage] = useState(10);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  

  const fetchLetters = async (page = 0) => {
    try {
      setLoading(true);
      const data = await letterApi.getLettersList({ 
        page, 
        limit: lettersPerPage 
      });
      
      if (page === 0) {
        setLetters(data.letters);
      } else {
        // For subsequent pages, replace letters array
        setLetters(data.letters);
      }
      
      setUserProgress(data.user_progress);
      setCurrentPage(page);
      setHasMorePages(data.has_more || false);
      setTotalCount(data.total_count || data.letters.length);
      
      // Auto-select current letter or first available letter only on initial load
      if (page === 0 && data.letters.length > 0) {
        const currentLetter = data.letters.find(
          letter => letter.letter_id === data.user_progress.current
        );
        const letterToSelect = currentLetter || data.letters[0];
        setSelectedLetter(letterToSelect);
        handleLetterSelect(letterToSelect);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const cleanLetterText = async (htmlContent) => {
    return htmlContent
      .replace(/<style[^>]*>.*?<\/style>/gis, '')
      .replace(/<script[^>]*>.*?<\/script>/gis, '')
      .replace(/<!DOCTYPE[^>]*>/gi, '')
      .replace(/<html[^>]*>/gi, '')
      .replace(/<\/html>/gi, '')
      .replace(/<head[^>]*>.*?<\/head>/gis, '')
      .replace(/<body[^>]*>/gi, '')
      .replace(/<\/body>/gi, '')
      .replace(/<div[^>]*class="[^"]*metadata[^"]*"[^>]*>.*?<\/div>/gis, '')
      .replace(/<div[^>]*class="[^"]*edit-notice[^"]*"[^>]*>.*?<\/div>/gis, '')
      .replace('<hr>', '');
  }

  // Handle letter selection
  const handleLetterSelect = async (letter) => {
    setSelectedLetter(letter);
    setLoadingContent(true);
    setLetterContent('');

    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        setError('User email not found');
        return;
      }

      const url = `${letterApi.getRawLetterUrl(letter.letter_id)}`;
      const response = await fetch(url);

      if (response.ok) {
        const htmlContent = await response.text();
        // Convert HTML to markdown for ReactMarkdown
        const turndownService = new TurndownService({
          headingStyle: 'atx',
          codeBlockStyle: 'fenced'
        });

        // Extract content from the .content div since it's already in markdown format
        const contentMatch = htmlContent.match(/<div[^>]*class="content"[^>]*>(.*?)<\/div>/gis);

        if (contentMatch && contentMatch[0]) {
          // Extract the content and clean it up
          const rawContent = contentMatch[0]
            .replace(/<div[^>]*class="content"[^>]*>/gi, '')
            .replace(/<\/div>/gi, '')
            .replace(/contenteditable="true"/gi, '')
            .trim();

          setLetterContent(rawContent);
        } else {
          // Fallback: use turndown for full HTML conversion
          const cleanedHtml = await cleanLetterText(htmlContent)
          const markdownContent = turndownService.turndown(cleanedHtml);
          setLetterContent(markdownContent);
        }
        setError(null);
      } else {
        setError(`Failed to load letter content: ${response.status}`);
      }
    } catch (err) {
      console.error('Error loading letter content:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setLoadingContent(false);
    }
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 0) {
      fetchLetters(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMorePages) {
      fetchLetters(currentPage + 1);
    }
  };

  // Use letters directly since they're already paginated from backend
  const currentLetters = letters;

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Handle taking quiz for a letter
  const handleTakeQuiz = (letterId) => {
    navigate(`/quiz/daily/${letterId}`);
  };

  // Handle quick jump to letter
  const handleQuickJump = async (e) => {
    e.preventDefault();
    const letterId = parseInt(quickJumpValue);
    
    if (!letterId || letterId <= 0) {
      return;
    }

    // Check if letter is accessible (within user's completed range)
    if (letterId > userProgress.completed && userProgress.completed > 0) {
      setError(`Letter ${letterId} not accessible - only completed up to letter ${userProgress.completed}`);
      return;
    }

    if (userProgress.completed === 0 && letterId !== 1) {
      setError('Only letter 1 is available - complete more letters first');
      return;
    }

    // Check if letter is in current page
    const letter = letters.find(l => l.letter_id === letterId);
    if (letter) {
      // Letter is in current page - select it directly
      await handleLetterSelect(letter);
      setQuickJumpValue('');
      return;
    }

    // Letter is not in current page - need to fetch the page containing it
    try {
      setLoading(true);
      setError(null);

      // Calculate which page contains this letter
      // Backend shows letters in descending order from last_completed
      // Page 0: last_completed down to (last_completed - 9)
      // Page 1: (last_completed - 10) down to (last_completed - 19), etc.
      
      const lastCompleted = userProgress.completed;
      const lettersFromEnd = lastCompleted - letterId; // How many letters back from the end
      const targetPage = Math.floor(lettersFromEnd / lettersPerPage);
      
      // Fetch the page containing the target letter
      const data = await letterApi.getLettersList({ 
        page: targetPage, 
        limit: lettersPerPage 
      });

      // Update page state
      setLetters(data.letters);
      setCurrentPage(targetPage);
      setHasMorePages(data.has_more || false);
      setTotalCount(data.total_count || data.letters.length);
      
      // Find and select the target letter
      const targetLetter = data.letters.find(l => l.letter_id === letterId);
      if (targetLetter) {
        setSelectedLetter(targetLetter);
        await handleLetterSelect(targetLetter);
        setQuickJumpValue('');
      } else {
        setError(`Letter ${letterId} not found or not accessible`);
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching letters:', error);
      setError(`Failed to load letters: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle navigation context and letter selection
  const getTargetLetter = (letters, userProgress, searchParams) => {
    const letterParam = searchParams.get('letter');
    if (letterParam) {
      const targetId = parseInt(letterParam);
      return letters.find(letter => letter.letter_id === targetId);
    }
    // Default to current progress letter or first available
    const currentLetter = letters.find(letter => letter.letter_id === userProgress.current);
    return currentLetter || letters[0];
  };

  // Single useEffect for component initialization
  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      setError('Please log in to access PrepLetters');
      return;
    }

    const initializeComponent = async () => {
      try {
        setLoading(true);
    
        const letterParam = searchParams.get('letter');
        let targetPage = 0;
    
        if (letterParam) {
          const targetId = parseInt(letterParam);
    
          // First fetch page 0 to get user_progress info
          const userData = await letterApi.getLettersList({ page: 0, limit: lettersPerPage });
          const lastCompleted = userData.user_progress.completed;
          const lettersFromEnd = lastCompleted - targetId;
          targetPage = Math.max(0, Math.floor(lettersFromEnd / lettersPerPage));
        }
    
        // Fetch the page that should contain the target letter (or page 0 if no param)
        const data = await letterApi.getLettersList({ page: targetPage, limit: lettersPerPage });
    
        setLetters(data.letters);
        setUserProgress(data.user_progress);
        setCurrentPage(targetPage);
        setHasMorePages(data.has_more || false);
        setTotalCount(data.total_count || data.letters.length);
    
        if (data.letters.length > 0) {
          let targetLetter;
          if (letterParam) {
            const targetId = parseInt(letterParam);
            targetLetter = data.letters.find(l => l.letter_id === targetId);
          }
          if (!targetLetter) {
            targetLetter = getTargetLetter(data.letters, data.user_progress, searchParams);
          }
          if (targetLetter) {
            setSelectedLetter(targetLetter);
            handleLetterSelect(targetLetter);
          }
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    

    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remove custom body styling since we now use standard layout

  if (error && error.includes('log in')) {
    return (
      <div className="prep-letters-container">
        <div className="premium-gate">
          <div className="premium-content">
            <h2>🔒 Premium Feature</h2>
            <p>PrepLetters is a premium feature. Please log in to access your letters.</p>
            <button className="btn btn-primary" onClick={() => {
              const currentPath = window.location.pathname + window.location.search;
              navigate(`/login?returnTo=${encodeURIComponent(currentPath)}`);
            }}>
              Log In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prep-letters-container">
      <div className="prep-letters-header">
        <h1>📚 PrepLetters</h1>
      </div>

      {error && !error.includes('log in') && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="prep-letters-content">
        {/* Sidebar */}
        <div className={`letters-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          {/* Sidebar Toggle Button */}
          <button 
            className="sidebar-toggle-btn"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>

          {/* Collapsed sidebar content */}
          {sidebarCollapsed && (
            <div className="collapsed-sidebar-content">
              <div className="collapsed-icon">
                <span>📚</span>
                <small>{userProgress.current}</small>
              </div>
              <div className="collapsed-icon">
                <span>🎯</span>
                <small>{selectedLetter.language}</small>
              </div>
              <div className="collapsed-icon">
                <span>📊</span>
                <small>{letters.length}</small>
              </div>
            </div>
          )}

          {/* Progress Summary */}
          <div className="progress-summary">
            <h3>Your Progress</h3>
            <div className="progress-stats">
              <div className="stat">
                <span className="stat-number">{userProgress.current}</span>
                <span className="stat-label">UPCOMING LETTER</span>
              </div>
            </div>
          </div>

          {/* Quick Jump */}
          <div className="quick-jump">
            <label>Jump to Letter:</label>
            <form onSubmit={handleQuickJump} className="input-group">
              <input
                type="number"
                className="form-control"
                placeholder="Enter letter #"
                value={quickJumpValue}
                onChange={(e) => setQuickJumpValue(e.target.value)}
                min="1"
                max={userProgress.current}
              />
              <button type="submit" className="btn">Go</button>
            </form>
          </div>

          {/* Letters List */}
          <div className="letters-list">
            <h4>Available Letters ({letters.length})</h4>
            {loading ? (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="letters-pagination">
                  <div className="pagination-info">
                    Page {currentPage + 1} {totalCount > 0 && `of ${Math.ceil(totalCount / lettersPerPage)}`}
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 0}
                    >
                      ← Previous
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleNextPage}
                      disabled={!hasMorePages}
                    >
                      Next →
                    </button>
                  </div>
                </div>
                
                <div className="letters-card-list">
                  {currentLetters.map((letter) => (
                    <div
                      key={letter.letter_id}
                      className={`letter-card ${selectedLetter?.letter_id === letter.letter_id ? 'active' : ''} ${letter.is_completed ? 'completed' : ''}`}
                      onClick={() => handleLetterSelect(letter)}
                    >
                      <div className="letter-card-number">#{letter.letter_id}</div>
                      <div className="letter-card-content">
                        <span className="letter-card-title">{letter.title}</span>
                        {letter.best_score !== undefined && letter.best_score !== null && (
                          <span className={`letter-card-score ${letter.best_score >= 80 ? 'excellent' : letter.best_score >= 60 ? 'good' : 'needs-work'}`}>
                            {Math.round(letter.best_score)}%
                          </span>
                        )}
                      </div>
                      <div className="letter-card-actions">
                        {letter.has_quiz && (
                          <span className="letter-card-quiz-badge">
                            <i className="bi bi-check-circle"></i>
                          </span>
                        )}
                        <i className="bi bi-chevron-right letter-card-arrow"></i>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className={`letter-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="letter-viewer">
            {selectedLetter ? (
              <>
                <div className="letter-content-header">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h2>#{selectedLetter.letter_id}: {selectedLetter.title}</h2>
                      <div className="letter-meta">
                        <span className="badge">{selectedLetter.language.toUpperCase()}</span>
                        {selectedLetter.is_completed && (
                          <span className="badge ms-2">Completed</span>
                        )}
                      </div>
                    </div>
                    <div className="letter-actions">
                      <div className="quiz-action-buttons">
                        <button 
                          className="btn btn-primary quiz-button"
                          onClick={() => handleTakeQuiz(selectedLetter.letter_id)}
                          title="Take quiz for this letter"
                        >
                          <i className="bi bi-play-circle me-1"></i>
                          Take Quiz
                        </button>
                        
                        {selectedLetter.has_quiz && (
                          <button 
                            className="btn btn-outline-secondary view-result-button"
                            onClick={() => navigate(`/quiz-history/daily_${selectedLetter.letter_id}`)}
                            title="View quiz results and history"
                          >
                            <i className="bi bi-bar-chart me-1"></i>
                            View Results
                          </button>
                        )}
                      </div>
                      
                      {/* Best Score Display */}
                      {selectedLetter.best_score !== undefined && selectedLetter.best_score !== null && (
                        <div className="best-quiz-score mt-2">
                          <div className="best-score-display">
                            <span className="score-icon">🏆</span>
                            <span className="score-text">Best Score: </span>
                            <span className={`score-value ${selectedLetter.best_score >= 80 ? 'excellent' : selectedLetter.best_score >= 60 ? 'good' : 'needs-work'}`}>
                              {Math.round(selectedLetter.best_score)}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="letter-content-container">
                  {loadingContent ? (
                    <div className="letter-content-loader">
                      <div className="content-spinner"></div>
                      <div className="content-loader-text">
                        <h4>Loading Letter Content</h4>
                        <p>#{selectedLetter.letter_id}: {selectedLetter.title}</p>
                      </div>
                    </div>
                  ) : letterContent ? (
                    <>
                      <div className="letter-markdown-content prose max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {letterContent}
                        </ReactMarkdown>
                      </div>
                      
                      {/* Footer Take Quiz Button */}
                      <div className="letter-content-footer">
                        <div className="footer-quiz-section">
                          <h5 className="footer-quiz-title">Ready to Test Your Understanding?</h5>
                          <p className="footer-quiz-description">
                            Take the quiz to reinforce what you've learned in this letter.
                          </p>
                          <button 
                            className="btn btn-success footer-quiz-button"
                            onClick={() => handleTakeQuiz(selectedLetter.letter_id)}
                          >
                            <i className="bi bi-lightning-charge me-2"></i>
                            Take Quiz Now
                          </button>
                        </div>
                      </div>
                       {/* Bug Report Link */}
                      <div className="prepletters-bug-report mt-4 text-center">
                        <a 
                          href={`/bug-report?page=prepletters&entity=prepletter-${selectedLetter?.letter_id}-${selectedLetter?.language}`} 
                          className="bug-report-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          🐛 Report a Bug
                        </a>
                      </div>
                    </>
                  ) : (
                    <div className="empty-content">
                      <p>Click a letter to view its content</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-letter-selected">
                <div className="empty-state">
                  <i className="bi bi-envelope-open"></i>
                  <h3>Select a PrepLetter</h3>
                  <p>Choose a letter from the sidebar to start reading</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default PrepLetters;
